import { useEffect, useState } from 'react';
import { Badge } from 'antd';

import { useAppSelector } from 'app/store';
import { useLazyGetAllReferencesQuery } from 'app/services/edpApiV1/references';

import { viewerModel } from 'entities/viewer';

import { dataForReferencesTable, sortingDate } from 'shared/helpers/utils';
import { RequestsSearch, TColumns } from 'shared/types/types';

import { TableInProgress } from 'features/tables';
import { RolesUser, SortCondition } from 'shared/enums';
import { usePullSort } from 'shared/hooks';
import { ONE_MINUTE } from 'shared/constants';
import { useIndicators } from 'features/indicators';
import { TPreparedReferencesTableData } from 'shared/types/reference.types';
import { SearchKeysNews } from 'shared/enums/news';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import RenderCellWithWarning from '../RenderCellWithWarning';

const ReferencesInProgress = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataReferences, setDataReferences] = useState<TPreparedReferencesTableData[]>();
  const [sortInfo, trigger] = usePullSort();
  const { role } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  // Добавляем пуллинг обновления списка  и обновляем индикаторы через хук useIndicators
  const [getAllReferencesTriger, resultAllReferences] = useLazyGetAllReferencesQuery({
    pollingInterval: ONE_MINUTE,
  });
  const { data } = resultAllReferences;

  const columns: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 120,
      render: value => <RenderCellWithWarning value={value} arr={data} currentRole={role} />,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: _ => ({
        onClick: () => trigger(),
      }),
    },
    {
      title: 'Вид справки',
      dataIndex: 'refTypeName',
      key: 'refTypeName',
    },
    {
      title: 'Период',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Получение',
      dataIndex: 'gettingMethod',
      key: 'gettingMethod',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <span className="d-flex">
          <Badge status={status.type} />
          {status.text}
        </span>
      ),
    },
  ];

  useEffect(() => {
    if (role === RolesUser.Consultant) {
      if (sortInfo.value === null)
        getAllReferencesTriger({
          type: 'myInWork',
          page: currentPage,
          size: currentSize,
          allAvailableOrganizations,
          ...search,
        });
      if (sortInfo.value === SortCondition.ASCEND)
        getAllReferencesTriger({
          type: 'myInWork',
          page: currentPage,
          size: currentSize,
          order: SortCondition.ASC,
          allAvailableOrganizations,
          ...search,
        });
      if (sortInfo.value === SortCondition.DESCEND)
        getAllReferencesTriger({
          type: 'myInWork',
          page: currentPage,
          size: currentSize,
          order: SortCondition.DESC,
          allAvailableOrganizations,
          ...search,
        });
    } else {
      if (sortInfo.value === null)
        getAllReferencesTriger({
          type: 'myInWork',
          page: currentPage,
          size: currentSize,
          ...search,
          allAvailableOrganizations,
        });
      if (sortInfo.value === SortCondition.ASCEND)
        getAllReferencesTriger({
          type: 'myInWork',
          page: currentPage,
          size: currentSize,
          order: SortCondition.ASC,
          allAvailableOrganizations,
          ...search,
        });
      if (sortInfo.value === SortCondition.DESCEND)
        getAllReferencesTriger({
          type: 'myInWork',
          page: currentPage,
          size: currentSize,
          order: SortCondition.DESC,
          allAvailableOrganizations,
          ...search,
        });
    }
  }, [role, currentPage, getAllReferencesTriger, sortInfo.value, currentSize, search, allAvailableOrganizations]);

  useEffect(() => {
    if (data) {
      const dataTable = dataForReferencesTable(data?.data);

      if (dataTable) {
        setDataReferences(dataTable);
      }
    }
  }, [data]);

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  // подписываемся на обновление индикаторов, после обновления списка  и отключаем пуллинг запроса индикаторов
  useIndicators(resultAllReferences.isSuccess, resultAllReferences.isFetching, false);

  return (
    <>
      {dataReferences && (
        <TableInProgress
          dataSource={dataReferences}
          columns={columns}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          currentSize={currentSize}
          setCurrentSize={setCurrentSize}
          total={data?.pagination?.total}
          onSearch={onSearch}
        />
      )}
    </>
  );
};

export default ReferencesInProgress;
